import type { SpApiRegion } from "@adpilot/shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { getApiErrorMessage } from "@/lib/api-error";
import { showPopupStatus } from "@/lib/oauth-popup-status";
import {
  fetchSpApiAccountConnectUrl,
  fetchSpApiConnectUrl,
  reauthorizeSpApiConnection,
} from "@/lib/sp-api";
import { parseTrustedSpApiOAuthMessage, type SpApiOAuthResultMessage } from "@/lib/sp-api-oauth";
import { receiveSpApiOAuthResult, SP_API_OAUTH_TIMEOUT_MS } from "@/lib/sp-api-oauth-relay";

export const SP_API_QUERY_KEY = ["amazon", "sp-api", "connections"] as const;

const POPUP_NAME = "adpilot-seller-central-authorization";
const POPUP_FEATURES = "popup=yes,width=640,height=760,resizable=yes,scrollbars=yes";

export type SpApiAuthorizationRequest =
  | { kind: "connect"; shopId?: string }
  /** Onboarding: the seller is named from the cached Ads grant, before any Shop exists. */
  | { kind: "connect-account"; selectionState: string; merchantId: string; region: SpApiRegion }
  | { kind: "reauthorize"; connectionId: string };

export type PendingSpApiAuthorization = SpApiAuthorizationRequest & {
  phase: "starting" | "waiting";
};

const SP_API_OAUTH_ERROR_KEYS: Record<string, string> = {
  "auth.required": "sellerCentral.oauthErrors.authRequired",
  "sp_api.oauth.callback_uri_invalid": "sellerCentral.oauthErrors.failed",
  "sp_api.oauth.denied": "sellerCentral.oauthErrors.denied",
  "sp_api.oauth.not_configured": "sellerCentral.oauthErrors.unavailable",
  "sp_api.oauth.request_invalid": "sellerCentral.oauthErrors.failed",
  "sp_api.oauth.seller_mismatch": "sellerCentral.oauthErrors.sellerMismatch",
  "sp_api.oauth.state_expired": "sellerCentral.oauthErrors.stateInvalid",
  "sp_api.oauth.state_invalid": "sellerCentral.oauthErrors.stateInvalid",
  "sp_api.oauth.unavailable_in_sandbox": "sellerCentral.oauthErrors.unavailable",
};

export function spApiOAuthErrorKey(code: string): string {
  return SP_API_OAUTH_ERROR_KEYS[code] ?? "sellerCentral.oauthErrors.failed";
}

export interface SpApiAuthorizationController {
  beginAuthorization: (request: SpApiAuthorizationRequest) => void;
  pendingAuthorization: PendingSpApiAuthorization | null;
  cancelAuthorization: () => void;
}

function startAuthorizationRequest(request: SpApiAuthorizationRequest) {
  switch (request.kind) {
    case "connect":
      return fetchSpApiConnectUrl(request.shopId);
    case "connect-account":
      return fetchSpApiAccountConnectUrl({
        selectionState: request.selectionState,
        merchantId: request.merchantId,
        region: request.region,
      });
    case "reauthorize":
      return reauthorizeSpApiConnection(request.connectionId);
  }
}

export function useSpApiAuthorization(): SpApiAuthorizationController {
  const { t } = useTranslation("settings");
  const queryClient = useQueryClient();
  const popupRef = useRef<Window | null>(null);
  const attemptRef = useRef(0);
  const redirectFallbackRef = useRef(false);
  const popupWatcherRef = useRef<number | null>(null);
  const timeoutRef = useRef<number | null>(null);
  const relayCleanupRef = useRef<(() => void) | null>(null);
  const [pendingAuthorization, setPendingAuthorization] =
    useState<PendingSpApiAuthorization | null>(null);

  const stopWaiting = useCallback(() => {
    if (popupWatcherRef.current !== null) window.clearInterval(popupWatcherRef.current);
    if (timeoutRef.current !== null) window.clearTimeout(timeoutRef.current);
    popupWatcherRef.current = null;
    timeoutRef.current = null;
    relayCleanupRef.current?.();
    relayCleanupRef.current = null;
  }, []);

  const finishAuthorization = useCallback(() => {
    stopWaiting();
    attemptRef.current += 1;
    popupRef.current = null;
    redirectFallbackRef.current = false;
    setPendingAuthorization(null);
  }, [stopWaiting]);

  const cancelAuthorization = useCallback(() => {
    const popup = popupRef.current;
    if (popup && !popup.closed) popup.close();
    finishAuthorization();
  }, [finishAuthorization]);

  /** Close the popup, leaving the error in it in case the browser keeps it open. */
  const failAuthorization = useCallback(
    (message: string) => {
      const popup = popupRef.current;
      if (popup && !popup.closed) {
        showPopupStatus(popup, {
          title: t("sellerCentral.callback.failed"),
          message,
          hint: t("sellerCentral.popup.closeHint"),
        });
      }
      cancelAuthorization();
      toast.error(message);
    },
    [cancelAuthorization, t],
  );

  const handleResult = useCallback(
    (result: SpApiOAuthResultMessage) => {
      const popup = popupRef.current;
      if (popup && !popup.closed) popup.close();
      finishAuthorization();
      if (result.success) {
        void queryClient.invalidateQueries({ queryKey: SP_API_QUERY_KEY });
        void queryClient.invalidateQueries({ queryKey: ["dashboard", "sales-traffic"] });
        toast.success(t("sellerCentral.connected"));
      } else {
        toast.error(t(spApiOAuthErrorKey(result.errorCode)));
      }
    },
    [finishAuthorization, queryClient, t],
  );

  const authorizationMutation = useMutation({
    mutationFn: ({ request }: { request: SpApiAuthorizationRequest; attempt: number }) =>
      startAuthorizationRequest(request),
    onSuccess: ({ redirectUrl }, { attempt }) => {
      if (attempt !== attemptRef.current) return;
      const popup = popupRef.current;
      if (!popup) {
        if (redirectFallbackRef.current) window.location.assign(redirectUrl);
        return;
      }
      if (popup.closed) {
        cancelAuthorization();
        toast.error(t("sellerCentral.oauthErrors.popupClosed"));
        return;
      }
      popup.location.replace(redirectUrl);
      setPendingAuthorization((current) => (current ? { ...current, phase: "waiting" } : current));
    },
    onError: async (error, { attempt }) => {
      const message = await getApiErrorMessage(error);
      if (attempt !== attemptRef.current) return;
      failAuthorization(message);
    },
  });

  const beginAuthorization = useCallback(
    (request: SpApiAuthorizationRequest) => {
      if (popupRef.current || redirectFallbackRef.current) return;
      const attempt = ++attemptRef.current;
      const popup = window.open("", POPUP_NAME, POPUP_FEATURES);
      popupRef.current = popup;
      redirectFallbackRef.current = popup === null;
      setPendingAuthorization({ ...request, phase: "starting" });

      if (popup) {
        showPopupStatus(popup, {
          title: t("sellerCentral.popup.openingTitle"),
          message: t("sellerCentral.popup.openingMessage"),
        });
        try {
          relayCleanupRef.current = receiveSpApiOAuthResult(popup.sessionStorage, (result) => {
            if (attempt === attemptRef.current) handleResult(result);
          });
        } catch {
          // Some browsers deny storage access even for an initially same-origin popup.
          relayCleanupRef.current = null;
        }
        // After a COOP navigation, .closed can be true for a still-open popup.
        // With a relay, wait for its result or explicit cancellation/timeout.
        if (!relayCleanupRef.current) {
          popupWatcherRef.current = window.setInterval(() => {
            if (!popup.closed) return;
            finishAuthorization();
            toast.error(t("sellerCentral.oauthErrors.popupClosed"));
          }, 500);
        }
      }
      timeoutRef.current = window.setTimeout(() => {
        failAuthorization(t("sellerCentral.oauthErrors.stateInvalid"));
      }, SP_API_OAUTH_TIMEOUT_MS);
      authorizationMutation.mutate({ request, attempt });
    },
    [authorizationMutation, failAuthorization, finishAuthorization, handleResult, t],
  );

  useEffect(() => {
    function handleMessage(event: MessageEvent): void {
      const popup = popupRef.current;
      if (!popup) return;
      const result = parseTrustedSpApiOAuthMessage(event, window.location.origin, popup);
      if (result) handleResult(result);
    }
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [handleResult]);

  useEffect(
    () => () => {
      stopWaiting();
      attemptRef.current += 1;
      const popup = popupRef.current;
      if (popup && !popup.closed) popup.close();
    },
    [stopWaiting],
  );

  return { beginAuthorization, pendingAuthorization, cancelAuthorization };
}
